import EUR_RATES_RAW from "./data/EUR.json";
import USD_RATES_RAW from "./data/USD.json";
import * as Currencies from "./currencies.js";

/**
 * @typedef {Object} FxQuote
 * @property {number} rate 1 from = rate to
 * @property {string} source
 * @property {string|null} period
 * @property {string} from
 * @property {string} to
 */

/**
 *
 * @version 2026-09-03
 * @author Patrik Harag
 */
export default class CurrencyService {

    static DEFAULT_CURRENCY = Currencies.DEFAULT_CURRENCY;
    static SUPPORTED_CURRENCIES = Currencies.SUPPORTED_CURRENCIES;

    /** @type {Map<string, {source: string, rates: Object<string, number>, periods: string[]}>|null} */
    static #tables = null;

    static getSupportedCurrencies() {
        return CurrencyService.SUPPORTED_CURRENCIES.slice();
    }

    static getDefaultCurrency() {
        return CurrencyService.DEFAULT_CURRENCY;
    }

    /**
     * Overlay API rates onto an existing (embedded) table.
     *
     * @param raw {object|string}
     */
    static mergeTable(raw) {
        CurrencyService.#ensureTables();
        let data = (typeof raw === 'string') ? JSON.parse(raw) : raw;
        if (data === null || data === undefined || data.currency === undefined || data.rates === undefined) {
            return;
        }
        let existing = CurrencyService.#tables.get(data.currency);
        if (existing === undefined) {
            CurrencyService.#registerTable(data);
            return;
        }
        let rates = Object.assign({}, existing.rates, data.rates);
        CurrencyService.#tables.set(data.currency, {
            source: (data.source !== undefined && data.source !== null && data.source !== '')
                    ? data.source
                    : existing.source,
            rates: rates,
            periods: Object.keys(rates).sort()
        });
    }

    /**
     * @param from {string}
     * @param to {string}
     * @param date {string} YYYY-MM-DD
     * @return {FxQuote}
     */
    static getQuote(from, to, date) {
        from = CurrencyService.#normalize(from);
        to = CurrencyService.#normalize(to);
        if (from === to) {
            return { rate: 1, source: 'identity', period: null, from, to };
        }

        let fromCzk = CurrencyService.#quoteToCzk(from, date);
        let toCzk = CurrencyService.#quoteToCzk(to, date);
        let rate = fromCzk.rate / toCzk.rate;

        if (from === CurrencyService.DEFAULT_CURRENCY) {
            return { rate, source: toCzk.source, period: toCzk.period, from, to };
        }
        if (to === CurrencyService.DEFAULT_CURRENCY) {
            return { rate, source: fromCzk.source, period: fromCzk.period, from, to };
        }
        return {
            rate,
            source: fromCzk.source,
            period: from + ' ' + fromCzk.period + ' / ' + to + ' ' + toCzk.period,
            from,
            to
        };
    }

    /**
     * @param transaction {Transaction}
     * @param target {string}
     * @return {Transaction}
     */
    static convertTransaction(transaction, target) {
        target = CurrencyService.#normalize(target);
        let from = transaction.getCurrency();
        if (from === target) {
            return transaction;
        }
        let quote = CurrencyService.getQuote(from, target, transaction.getDate());
        return transaction.asConverted(transaction.getValue() * quote.rate, target, quote);
    }

    /**
     * @param transactions {Transaction[]}
     * @param target {string}
     * @return {Transaction[]}
     */
    static convertTransactions(transactions, target) {
        return transactions.map(t => CurrencyService.convertTransaction(t, target));
    }

    /**
     * @param transactions {Transaction[]}
     * @return {Set<string>}
     */
    static collectCurrencies(transactions) {
        let currencies = new Set();
        for (let t of transactions) {
            currencies.add(t.getCurrency());
        }
        return currencies;
    }

    /**
     * @param currencies {Iterable<string>}
     * @return {string[]}
     */
    static orderCurrencies(currencies) {
        let preferred = CurrencyService.SUPPORTED_CURRENCIES;
        let remaining = new Set(currencies);
        let ordered = [];
        for (let c of preferred) {
            if (remaining.has(c)) {
                ordered.push(c);
                remaining.delete(c);
            }
        }
        Array.from(remaining).sort().forEach(c => ordered.push(c));
        return ordered;
    }

    static #normalize(currency) {
        if (currency === undefined || currency === null || currency === '') {
            return CurrencyService.DEFAULT_CURRENCY;
        }
        return currency;
    }

    /**
     * @param currency {string}
     * @param date {string}
     * @return {{rate: number, source: string, period: string|null}}
     */
    static #quoteToCzk(currency, date) {
        if (currency === CurrencyService.DEFAULT_CURRENCY) {
            return { rate: 1, source: 'identity', period: null };
        }
        CurrencyService.#ensureTables();
        let table = CurrencyService.#tables.get(currency);
        if (table === undefined) {
            throw 'Unsupported currency: ' + currency;
        }
        let period = CurrencyService.#resolvePeriod(table.periods, date);
        return { rate: table.rates[period], source: table.source, period };
    }

    /**
     * @param periods {string[]} sorted YYYY-MM
     * @param date {string}
     * @return {string}
     */
    static #resolvePeriod(periods, date) {
        let month = date.substring(0, 7);
        let chosen = periods[0];
        for (let period of periods) {
            if (period <= month) {
                chosen = period;
            } else {
                break;
            }
        }
        return chosen;
    }

    static #ensureTables() {
        if (CurrencyService.#tables !== null) {
            return;
        }
        CurrencyService.#tables = new Map();
        CurrencyService.#registerTable(EUR_RATES_RAW);
        CurrencyService.#registerTable(USD_RATES_RAW);
    }

    static #registerTable(raw) {
        let data = (typeof raw === 'string') ? JSON.parse(raw) : raw;
        let periods = Object.keys(data.rates).sort();
        CurrencyService.#tables.set(data.currency, {
            source: data.source,
            rates: data.rates,
            periods: periods
        });
    }
}
